import { integer } from '../kernel/primitives.ts';

/** Autonomous goals a companion can pursue. Hold means acting on nothing this tick. */
export type GoalKind = 'engage' | 'guard' | 'hold' | 'withdraw';
export const GOALS: readonly GoalKind[] = ['engage', 'guard', 'hold', 'withdraw'];

/** Explicit temperament traits. Integers 0–10; higher boldness seeks danger, higher loyalty stays near the PC. */
export type Temperament = Readonly<{ boldness: number; loyalty: number }>;

/** A duty the companion will not break. Forbidden goals are vetoed with a recorded reason, never silently dropped. */
export type Obligation = Readonly<{
  id: string;
  description: string;
  forbiddenGoals: readonly GoalKind[];
  /** When present, this duty applies only against these identities. */
  against?: readonly string[] | undefined;
  /**
   * Voiced paraphrase of this duty in the companion's own voice, spoken when the
   * duty forces a refusal. The raw description is a contract record and must never
   * reach dialogue unvoiced.
   */
  refusal?: string | undefined;
}>;

/**
 * A contract boundary the companion enforces themselves.
 * - `danger-limit`: danger above this level forces withdrawal.
 * - `refuse-goal`: this goal is never chosen.
 * - `non-negotiable-topic`: discussion of this goal never changes preferences.
 */
export type ContractBoundary =
  | Readonly<{ id: string; kind: 'danger-limit'; dangerLimit: number }>
  | Readonly<{ id: string; kind: 'refuse-goal'; goal: GoalKind }>
  | Readonly<{ id: string; kind: 'non-negotiable-topic'; topic: GoalKind }>;

export type RefusalReason =
  | 'danger-exceeds-temperament'
  | 'obligation-forbids'
  | 'boundary-non-negotiable'
  | 'temperament-declines'
  | 'already-refused';

/** Every refusal is recorded with its reason so a withdrawal or declined discussion is always explainable. */
export type RefusalRecord = Readonly<{ topic: string; reason: RefusalReason; sequence: number }>;

export type CompanionContract = {
  companionId: string;
  temperament: Temperament;
  obligations: Obligation[];
  boundaries: ContractBoundary[];
  /** Approach weights 0–10 per goal. Discussion changes these only when the companion accepts. */
  preferences: Record<GoalKind, number>;
  refusals: RefusalRecord[];
  discussionSequence: number;
  agreedApproach?: GoalKind | undefined;
};

export type ContractInput = Readonly<{
  companionId: string;
  temperament: Temperament;
  obligations?: readonly Obligation[];
  boundaries?: readonly ContractBoundary[];
  preferences?: Readonly<Partial<Record<GoalKind, number>>>;
}>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

function trait(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > 10) {
    throw new Error(`${label} must be an integer from 0 to 10`);
  }
  return value;
}

function weight(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > 10) {
    throw new Error(`${label} must be an integer from 0 to 10`);
  }
  return value;
}

function assertGoal(value: unknown): asserts value is GoalKind {
  if (value !== 'engage' && value !== 'guard' && value !== 'hold' && value !== 'withdraw') {
    throw new Error(`Unknown companion goal ${String(value)}`);
  }
}

function checkObligation(value: unknown): asserts value is Obligation {
  if (!isRecord(value) || typeof value.id !== 'string' || !value.id || typeof value.description !== 'string' || !value.description || !Array.isArray(value.forbiddenGoals)) {
    throw new Error('An obligation needs an id, a description and a forbidden-goal list');
  }
  for (const goal of value.forbiddenGoals) assertGoal(goal);
  if (value.against !== undefined && (!Array.isArray(value.against) || !value.against.length || value.against.some(id => typeof id !== 'string' || !id))) throw new Error('A contextual obligation needs target identities');
  if (value.refusal !== undefined && (typeof value.refusal !== 'string' || !value.refusal)) throw new Error('An obligation refusal line must be non-empty text');
}

function checkBoundary(value: unknown): asserts value is ContractBoundary {
  if (!isRecord(value) || typeof value.id !== 'string' || !value.id || typeof value.kind !== 'string') {
    throw new Error('A contract boundary needs an id and a kind');
  }
  if (value.kind === 'danger-limit') {
    if (!Number.isSafeInteger(value.dangerLimit) || Number(value.dangerLimit) < 0) {
      throw new Error('A danger limit must be a non-negative integer');
    }
    return;
  }
  if (value.kind === 'refuse-goal') {
    assertGoal(value.goal);
    return;
  }
  if (value.kind === 'non-negotiable-topic') {
    assertGoal(value.topic);
    return;
  }
  throw new Error(`Unknown contract boundary kind ${value.kind}`);
}

/**
 * Create a companion contract. All weights and traits are validated integers;
 * the returned contract is an isolated copy the caller cannot mutate from outside.
 */
export function createContract(input: ContractInput): CompanionContract {
  if (!isRecord(input) || typeof input.companionId !== 'string' || !input.companionId) {
    throw new Error('A companion contract needs a companion id');
  }
  if (!isRecord(input.temperament)) throw new Error('A companion contract needs an explicit temperament');
  const temperament = {
    boldness: trait(input.temperament.boldness, 'Boldness'),
    loyalty: trait(input.temperament.loyalty, 'Loyalty'),
  };
  const obligations = [...(input.obligations ?? [])];
  const boundaries = [...(input.boundaries ?? [])];
  for (const obligation of obligations) checkObligation(obligation);
  for (const boundary of boundaries) checkBoundary(boundary);
  const ids = [...obligations.map(obligation => obligation.id), ...boundaries.map(boundary => boundary.id)];
  if (new Set(ids).size !== ids.length) throw new Error('Obligation and boundary ids must be unique within a contract');
  const preferences: Record<GoalKind, number> = {
    engage: weight(input.preferences?.engage ?? 5, 'Engage preference'),
    guard: weight(input.preferences?.guard ?? 5, 'Guard preference'),
    hold: weight(input.preferences?.hold ?? 5, 'Hold preference'),
    withdraw: weight(input.preferences?.withdraw ?? 5, 'Withdraw preference'),
  };
  integer(preferences.engage);
  return {
    companionId: input.companionId,
    temperament,
    obligations: structuredClone(obligations) as Obligation[],
    boundaries: structuredClone(boundaries) as ContractBoundary[],
    preferences,
    refusals: [],
    discussionSequence: 0,
  };
}

/** Append a refusal record. Every refusal carries its reason; nothing about a refusal is silent. */
export function recordRefusal(contract: CompanionContract, topic: string, reason: RefusalReason): CompanionContract {
  if (!topic) throw new Error('A refusal needs a topic');
  const next = structuredClone(contract);
  next.discussionSequence = integer(next.discussionSequence) + 1;
  next.refusals.push({ topic, reason, sequence: next.discussionSequence });
  return next;
}

/** Goals this contract forbids outright, from obligations and refuse-goal boundaries. */
export function forbiddenGoals(contract: Readonly<CompanionContract>, targetId?: string): GoalKind[] {
  const forbidden = new Set<GoalKind>();
  for (const obligation of contract.obligations) {
    if (obligation.against && (!targetId || !obligation.against.includes(targetId))) continue;
    for (const goal of obligation.forbiddenGoals) forbidden.add(goal);
  }
  for (const boundary of contract.boundaries) if (boundary.kind === 'refuse-goal') forbidden.add(boundary.goal);
  return [...GOALS.filter(goal => forbidden.has(goal))];
}

/** The lowest danger-limit boundary, or null when the contract sets no danger limit. */
export function dangerLimit(contract: Readonly<CompanionContract>): number | null {
  let limit: number | null = null;
  for (const boundary of contract.boundaries) {
    if (boundary.kind !== 'danger-limit') continue;
    if (limit === null || boundary.dangerLimit < limit) limit = boundary.dangerLimit;
  }
  return limit;
}

/** True when the topic was refused before. A refused topic never flips on repetition. */
export function wasRefused(contract: Readonly<CompanionContract>, topic: string): boolean {
  return contract.refusals.some(refusal => refusal.topic === topic);
}
